import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2, MapPin, Car, RefreshCw, Hammer, SlidersHorizontal, Send, CheckCircle2, Lock, Clock, Users, Star,
  ChevronLeft, ChevronRight,
} from "lucide-react";
import localApi from "@/lib/localApi";
import { fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const FILTERS = [
  { key: "available", label: "Available" },
  { key: "my_bids", label: "Jobs I've Bid On" },
];

export default function JobBoardPanel({ onLoaded, showHeader = true }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [statusFilter, setStatusFilter] = useState("available");
  const [page, setPage] = useState(1);
  const [bidJob, setBidJob] = useState(null);
  const [bidForm, setBidForm] = useState({ amount: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async (pg, filter) => {
    setLoading(true);
    setNeedsSetup(false);
    try {
      const res = await localApi.get("/marketplace/job-board", {
        params: { page: pg, per_page: 10, status_filter: filter },
      });
      setData(res.data);
      setPage(res.data.page || 1);
      onLoaded && onLoaded(res.data);
    } catch (e) {
      if (e?.response?.status === 403) setNeedsSetup(true);
      else toast.error("Could not load the job board");
      onLoaded && onLoaded({ jobs: [], new_24h: 0 });
    } finally {
      setLoading(false);
    }
  }, [onLoaded]);

  useEffect(() => { load(1, "available"); /* eslint-disable-next-line */ }, []);

  const changeFilter = (f) => { setStatusFilter(f); setPage(1); load(1, f); };
  const goPage = (pg) => { setPage(pg); load(pg, statusFilter); };

  const openBid = (job) => {
    if (!data?.entitled) { toast.error("Subscribe to Pitcrew Pro to send estimates"); navigate("/billing"); return; }
    setBidForm({ amount: "", message: "" });
    setBidJob(job);
  };

  const submitBid = async () => {
    setSubmitting(true);
    try {
      await localApi.post(`/marketplace/requests/${bidJob.id}/bid`, {
        message: bidForm.message,
        amount: bidForm.amount === "" ? null : Number(bidForm.amount),
      });
      toast.success("Estimate sent — the client will be notified");
      setBidJob(null);
      load(page, statusFilter);
    } catch (e) {
      if (e?.response?.status === 402) { toast.error("Your trial has ended — subscribe to send estimates"); navigate("/billing"); }
      else toast.error(e?.response?.data?.detail || "Could not send estimate");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !data) return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>;

  if (needsSetup) {
    return (
      <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-10 text-center max-w-lg mx-auto" data-testid="jobboard-setup-prompt">
        <div className="h-12 w-12 rounded-xl bg-amber-500/20 flex items-center justify-center mx-auto mb-4"><SlidersHorizontal className="h-6 w-6 text-amber-400" /></div>
        <h3 className="font-head font-bold text-xl text-slate-50">Set up your service area</h3>
        <p className="text-slate-400 mt-2">Flag yourself as a mechanic and add your ZIP code in Settings to see open jobs within your radius.</p>
        <Button data-testid="goto-settings-marketplace" onClick={() => navigate("/settings")} className="mt-6 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">Go to Settings</Button>
      </div>
    );
  }

  const totalPages = data?.total_pages || 1;

  return (
    <div className="space-y-4">
      {showHeader && (
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="font-head font-bold text-xl text-slate-50 flex items-center gap-2"><Hammer className="h-5 w-5 text-amber-400" /> Open Jobs Near You</h2>
          <span className="text-sm text-slate-400" data-testid="jobboard-summary">within {data?.radius_miles} mi of {data?.service_zip}</span>
          <Button variant="ghost" size="sm" data-testid="refresh-jobs" onClick={() => load(page, statusFilter)} className="ml-auto text-slate-400 hover:text-amber-400"><RefreshCw className="h-4 w-4 mr-1.5" /> Refresh</Button>
        </div>
      )}

      {data && !data.entitled && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 flex items-center gap-3" data-testid="jobboard-paywall">
          <Lock className="h-5 w-5 text-amber-400 shrink-0" />
          <p className="text-sm text-slate-300 flex-1">Your trial has ended. Subscribe to <b>Pitcrew Pro</b> to send estimates and use AI tools.</p>
          <Button data-testid="paywall-subscribe" onClick={() => navigate("/billing")} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold shrink-0">Subscribe</Button>
        </div>
      )}

      {/* Status filter tabs */}
      <div className="flex items-center gap-2" data-testid="jobboard-filters">
        {FILTERS.map((f) => {
          const active = statusFilter === f.key;
          const cnt = f.key === "available" ? data?.available_count : data?.my_bids_count;
          return (
            <button
              key={f.key}
              data-testid={`jobboard-filter-${f.key}`}
              onClick={() => changeFilter(f.key)}
              className={`text-sm font-semibold rounded-lg px-3.5 py-1.5 border transition-colors duration-150 ${active
                ? "bg-amber-500 text-slate-950 border-amber-500"
                : "bg-slate-900 text-slate-400 border-slate-800 hover:text-slate-200 hover:border-slate-700"}`}
            >
              {f.label}
              {cnt != null && <span className={`ml-2 text-xs ${active ? "text-slate-800" : "text-slate-500"}`}>{cnt}</span>}
            </button>
          );
        })}
      </div>

      <div className="rounded-lg border border-slate-800 bg-slate-900/60 px-4 py-2.5 text-xs text-slate-500 flex items-center gap-2">
        <Lock className="h-3.5 w-3.5" /> Client contact stays private. Send an estimate — you'll get their details only if they accept your bid.
      </div>

      {(!data?.jobs || data.jobs.length === 0) ? (
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-16 text-center" data-testid="jobboard-empty">
          <Hammer className="h-10 w-10 text-slate-700 mx-auto mb-3" />
          <p className="text-slate-400">{statusFilter === "my_bids" ? "You haven't bid on any jobs yet." : "No open jobs in your area right now. Check back soon."}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="jobboard-grid">
          {data.jobs.map((j) => {
            const vehicle = [j.vehicle_year, j.vehicle_make, j.vehicle_model].filter(Boolean).join(" ");
            return (
              <div key={j.id} data-testid={`job-card-${j.id}`} className="rounded-xl border border-slate-800 bg-slate-900 p-5 flex flex-col transition-transform duration-200 hover:-translate-y-1">
                <div className="flex items-start justify-between">
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-full px-2.5 py-1">
                    <MapPin className="h-3 w-3" /> {j.distance_miles} mi · {j.zip_code}
                  </span>
                  <span className="text-[11px] text-slate-600">{fmtDate(j.created_at)}</span>
                </div>
                <p className="font-semibold text-slate-100 mt-3 flex items-center gap-2"><Car className="h-4 w-4 text-slate-500" /> {vehicle || "Vehicle not specified"}</p>
                <p className="text-sm text-slate-400 mt-2 flex-1">{j.issue || "No description provided."}</p>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-1.5 text-[11px] text-slate-500"><Users className="h-3 w-3" /> {j.bid_count || 0} estimate{j.bid_count === 1 ? "" : "s"} sent</div>
                  {j.client_rating?.avg ? (
                    <span data-testid={`job-client-rating-${j.id}`} className="inline-flex items-center gap-1 text-[11px] text-slate-400">
                      Client <Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {j.client_rating.avg.toFixed(1)} ({j.client_rating.count})
                    </span>
                  ) : (
                    <span data-testid={`job-client-new-${j.id}`} className="text-[11px] text-slate-600">New client</span>
                  )}
                </div>
                {j.my_bid_status === "accepted" ? (
                  <Button data-testid={`accepted-job-${j.id}`} onClick={() => navigate("/my-bids")} className="mt-3 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"><CheckCircle2 className="h-4 w-4 mr-1.5" /> Accepted — view details</Button>
                ) : j.my_bid_status === "pending" ? (
                  <Button data-testid={`bid-sent-${j.id}`} disabled className="mt-3 bg-slate-800 text-slate-400 font-semibold"><Clock className="h-4 w-4 mr-1.5" /> Estimate sent</Button>
                ) : (
                  <Button data-testid={`bid-job-${j.id}`} onClick={() => openBid(j)} className="mt-3 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"><Send className="h-4 w-4 mr-1.5" /> Send Estimate</Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {data && data.total > 0 && (
        <div className="flex items-center justify-between pt-2" data-testid="jobboard-pagination">
          <span className="text-xs text-slate-500" data-testid="jobboard-page-label">Page {data.page} of {totalPages} · {data.total} job{data.total === 1 ? "" : "s"}</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" data-testid="jobboard-prev" disabled={loading || data.page <= 1}
              onClick={() => goPage(data.page - 1)}
              className="bg-slate-900 border-slate-700 text-slate-200 hover:bg-slate-800 disabled:opacity-40">
              <ChevronLeft className="h-4 w-4 mr-1" /> Previous
            </Button>
            <Button variant="outline" size="sm" data-testid="jobboard-next" disabled={loading || data.page >= totalPages}
              onClick={() => goPage(data.page + 1)}
              className="bg-slate-900 border-slate-700 text-slate-200 hover:bg-slate-800 disabled:opacity-40">
              Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      <Dialog open={!!bidJob} onOpenChange={(o) => !o && setBidJob(null)}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-slate-100 font-head">Send an Estimate</DialogTitle>
            <DialogDescription className="text-slate-500">Give the client a ballpark quote and a note. Your contact is shared only if they accept.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Estimated price ($)</Label><Input data-testid="bid-amount-input" type="number" step="0.01" value={bidForm.amount} onChange={(e) => setBidForm((f) => ({ ...f, amount: e.target.value }))} placeholder="e.g. 180" className="h-10 bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50" /></div>
            <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Message to client</Label><Textarea data-testid="bid-message-input" value={bidForm.message} onChange={(e) => setBidForm((f) => ({ ...f, message: e.target.value }))} placeholder="What's included, availability, warranty…" className="bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBidJob(null)} className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700">Cancel</Button>
            <Button data-testid="submit-bid-button" onClick={submitBid} disabled={submitting} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">{submitting && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Send Estimate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
